import { useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { motion, useReducedMotion } from "framer-motion";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { api, type StateRow } from "@/lib/api";
import { EcosystemMap } from "@/components/EcosystemMap";
import { Journey } from "@/components/Journey";
import { PathFinder } from "@/components/PathFinder";
import { StatesPanel } from "@/components/StatesPanel";
import { Browse, type BrowseState } from "@/components/Browse";
import { PhotoCorner } from "@/components/PhotoCorner";

/**
 * Home = "The Atlas" — editorial magazine layout.
 *
 * Masthead → Anveṣaṇa search (centered, full-width) → vārtā ticker →
 * map + states rail → nav index → ministries → personas → journey →
 * compass → states panel → ledger. Every pre-existing section kept.
 */

const DEFAULT_BROWSE: BrowseState = {
  q: "", level: "", state: "", audience: "", benefit_type: "", source: "",
  offset: 0, limit: 20,
};

interface Props {
  onOpenScheme: (id: string) => void;
  onAskAi: (prompt: string) => void;
  onOpenPalette: () => void;
}

export function HomePage({ onOpenScheme, onAskAi, onOpenPalette }: Props) {
  const [browse, setBrowse] = useState<BrowseState>(DEFAULT_BROWSE);
  const { data: stats } = useApi(() => api.stats(), []);
  const { data: states } = useApi(() => api.states(), []);
  const reduce = useReducedMotion();
  const [, navigate] = useLocation();

  const ticker = useMemo(
    () =>
      stats?.ecosystem_headlines?.map((h) => (
        <span key={h.label} className="inline-flex items-center gap-3 shrink-0">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300/85">vārtā</span>
          <strong className="font-display text-cream-50">{h.value}</strong>
          <span className="text-cream-100/60">{h.label}</span>
          <span className="text-amber-300/40">❧</span>
        </span>
      )) ?? [],
    [stats],
  );

  return (
    <>
      {/* =====================================================
          MASTHEAD — nameplate only, nothing crammed in
         ===================================================== */}
      <section className="relative">
        <div aria-hidden className="absolute inset-0 hero-wash pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-5 pt-8 pb-5">
          <div className="flex items-center justify-between gap-6 mb-6">
            <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-cream-100/55">
              <span className="text-amber-300/85">N°</span>{" "}
              {new Date().getFullYear()}{" "}
              <span className="text-cream-100/25">/</span>{" "}
              {String(new Date().getMonth() + 1).padStart(2, "0")}
            </span>
            <TricolourRibbon />
          </div>

          <h1
            className="font-editorial font-medium text-center leading-[0.9] tracking-[-0.02em] text-cream-50
                       text-[clamp(2.4rem,6vw,4.8rem)]"
            style={{ fontVariationSettings: '"opsz" 144' }}
          >
            The <span className="italic text-amber-300">Founder’s</span> Atlas
            <span className="block font-sans font-normal text-[11px] tracking-[0.55em] uppercase text-amber-300/85 mt-4">
              of India
            </span>
          </h1>

          <p className="text-center font-editorial italic text-[16px] leading-[1.5] text-cream-100/70 max-w-[560px] mx-auto mt-5">
            {stats ? stats.total.toLocaleString("en-IN") : "1,996"} schemes, grants, loans,
            incubators and communities — indexed, cross-referenced, searchable in English.
          </p>

          {/* Anveṣaṇa — centered, full-width, unmissable */}
          <motion.button
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            onClick={onOpenPalette}
            className="group foil mt-7 w-full max-w-2xl mx-auto flex items-center gap-4 rounded-sm hairline
                       bg-gradient-to-br from-amber-400/[0.06] to-coral-500/[0.04]
                       hover:from-amber-400/[0.16] hover:to-coral-500/[0.12]
                       px-5 py-4 text-left transition"
          >
            <span className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full hairline bg-amber-400/10 group-hover:bg-amber-400/20 transition-colors">
              <Sparkles className="w-4 h-4 text-amber-300 group-hover:rotate-12 transition-transform" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-mono text-[9.5px] uppercase tracking-[0.4em] text-amber-300">anveṣaṇa</span>
                <kbd className="!bg-plum-950/60 !border-amber-400/20 text-amber-300 !text-[10px]">⌘K</kbd>
              </div>
              <div className="font-editorial italic text-[18px] text-cream-50 group-hover:text-amber-200 transition-colors leading-tight truncate">
                Search schemes, states, ministries, founders…
              </div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-amber-300/70 group-hover:text-amber-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
          </motion.button>
        </div>
      </section>

      {/* =====================================================
          VĀRTĀ TICKER — full-bleed, under masthead so you can't miss it
         ===================================================== */}
      {ticker.length > 0 && (
        <div className="border-y-2 border-double border-amber-400/25 bg-plum-950/70 overflow-hidden">
          <div className="ticker py-3 text-[13px] whitespace-nowrap">
            <div className="flex items-center gap-8 px-6 shrink-0">{ticker}</div>
            <div className="flex items-center gap-8 px-6 shrink-0" aria-hidden>{ticker}</div>
          </div>
        </div>
      )}

      {/* =====================================================
          CHAPTER I — The map, mounted like a print
         ===================================================== */}
      <section id="map" className="relative max-w-6xl mx-auto px-5 pt-10 pb-8">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <p className="section-eyebrow mb-1.5">plate i · the republic, in motion</p>
            <h2 className="section-title">
              Every state, <span className="font-serif italic text-amber-300">one map</span>.
            </h2>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream-100/55">
            ⌘/ctrl · scroll to zoom · click to walk
          </span>
        </div>

        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-5 items-stretch">
          <motion.figure
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="relative rounded-[0.5rem] hairline bg-plum-900/40 overflow-hidden shadow-[0_32px_90px_-34px_rgba(0,0,0,0.85)] h-[520px]"
          >
            <div className="absolute inset-0">
              <EcosystemMap onOpenScheme={onOpenScheme} standalone />
            </div>
            <PhotoCorner size={22} inset={14} thickness={1.5} color="rgba(255,183,3,0.85)" />
          </motion.figure>

          {/* Top-activity states rail */}
          <div className="relative rounded-[0.5rem] hairline bg-plum-900/30 p-5 h-[520px] overflow-auto">
            <div className="flex items-baseline justify-between mb-3">
              <p className="section-eyebrow">top activity</p>
              <Link href="#states" className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300/80 hover:text-amber-200">
                see all →
              </Link>
            </div>
            <ul className="divide-y divide-amber-400/10">
              {(states?.items ?? []).slice(0, 12).map((s: StateRow, i: number) => (
                <li key={s.state}>
                  <button
                    onClick={() => navigate(`/state/${slugify(s.state)}`)}
                    className="w-full flex items-center justify-between py-2.5 group"
                  >
                    <span className="flex items-center gap-3 min-w-0">
                      <span className="font-serif italic text-amber-300/55 text-[12px] w-6 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-display text-[14px] text-cream-50 group-hover:text-amber-200 transition truncate">
                        {s.state}
                      </span>
                    </span>
                    <span className="font-mono text-[11px] text-cream-100/50 shrink-0 ml-3">
                      {s.total}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* =====================================================
          QUICK INDEX — 6 ordinal cards, single horizontal row
         ===================================================== */}
      <section className="max-w-6xl mx-auto px-5 pb-10">
        <p className="section-eyebrow mb-3">table of contents</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
          {NAV_CARDS.map((n) => {
            const inner = (
              <>
                <span className="editorial-ordinal text-base leading-none">
                  N° <span className="italic">{n.ordinal}</span>
                </span>
                <div className="mt-5">
                  <div className="font-editorial italic text-[15px] text-cream-50 group-hover:text-amber-200 leading-tight transition-colors">
                    {n.label}
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-cream-100/45 mt-1">
                    {n.hint}
                  </div>
                </div>
              </>
            );
            const cls = "group flex flex-col justify-between rounded-sm hairline bg-plum-900/30 hover:bg-plum-900/70 transition p-4 text-left h-[130px]";
            return n.href.startsWith("#") ? (
              <button
                key={n.label}
                onClick={() => document.getElementById(n.href.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className={cls}
              >
                {inner}
              </button>
            ) : (
              <Link key={n.label} href={n.href} className={cls}>
                {inner}
              </Link>
            );
          })}
        </div>

        {/* stat strip below nav */}
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2 rounded-sm hairline bg-plum-900/25 px-4 py-3">
          {[
            { k: stats ? stats.total.toLocaleString("en-IN") : "…", l: "records" },
            { k: stats ? `${stats.states_covered}+` : "…", l: "states" },
            { k: stats ? ((stats.by_audience.startup || 0) + (stats.by_audience.msme || 0) + (stats.by_audience["small-biz"] || 0)).toLocaleString("en-IN") : "…", l: "for founders" },
            { k: stats ? (stats.by_benefit.equity || 0).toLocaleString("en-IN") : "…", l: "equity rails" },
          ].map((s) => (
            <div key={s.l} className="text-center">
              <div className="font-display text-[17px] font-semibold text-cream-50">{s.k}</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-cream-100/55 mt-0.5">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* =====================================================
          MINISTRIES — 12 seals
         ===================================================== */}
      <section id="ministries" className="max-w-6xl mx-auto px-5 py-16">
        <div className="mb-8">
          <p className="section-eyebrow mb-2">chapter ii · bureau</p>
          <h2 className="section-title">
            The <span className="font-serif italic text-amber-300">bureau of bureaus</span>.
          </h2>
          <p className="text-cream-100/60 mt-2 max-w-xl text-sm">
            Click a seal to walk the hall — every scheme under that ministry, vārtā and all.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {MINISTRY_TILES.map((t) => (
            <Link
              key={t.slug}
              href={`/ministry/${t.slug}`}
              className="group relative rounded-2xl border border-amber-400/15 hover:border-amber-400/50 bg-plum-900/40 hover:bg-plum-900/75 p-5 transition flex items-center gap-3"
            >
              <span
                className="w-12 h-12 rounded-full flex items-center justify-center border font-serif italic text-sm shrink-0"
                style={{
                  borderColor: "rgba(230,57,70,0.5)",
                  background: "rgba(15,10,26,0.65)",
                  color: "#ffb703",
                  boxShadow: "inset 0 0 0 2px rgba(230,57,70,0.15)",
                }}
              >
                {t.sigil}
              </span>
              <div className="min-w-0">
                <div className="font-display text-[14px] font-semibold text-cream-50 group-hover:text-amber-200 truncate transition-colors">
                  {t.name}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cream-100/50">
                  hall · {t.slug}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* =====================================================
          PERSONAS — 9 founder archetypes
         ===================================================== */}
      <section id="personas" className="max-w-6xl mx-auto px-5 py-16">
        <div className="mb-8">
          <p className="section-eyebrow mb-2">chapter iii · founders</p>
          <h2 className="section-title">
            Nine <span className="font-serif italic text-amber-300">founders</span>.
          </h2>
          <p className="text-cream-100/60 mt-2 max-w-xl text-sm">
            We wrote the stack for nine archetypes. Click the one that sounds like you — or none.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {PERSONA_TILES.map((p, i) => (
            <Link
              key={p.slug}
              href={`/persona/${p.slug}`}
              className="group relative overflow-hidden rounded-2xl border border-amber-400/15 hover:border-amber-400/50 bg-plum-900/40 hover:bg-plum-900/75 p-5 transition"
            >
              <div aria-hidden className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl opacity-60"
                   style={{ background: `radial-gradient(circle, ${p.tint}, transparent 70%)` }} />
              <div className="relative">
                <div className="flex items-center justify-between">
                  <span className="font-serif italic text-amber-300/70 text-sm">
                    no. {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream-100/55">
                    {p.tag}
                  </span>
                </div>
                <h3 className="font-display text-[16px] font-semibold text-cream-50 group-hover:text-amber-200 mt-2 leading-snug transition-colors">
                  {p.label}
                </h3>
                <p className="text-[12px] text-cream-100/55 mt-1.5 leading-snug line-clamp-2">{p.teaser}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Existing chapters preserved — Journey, Compass, States, Browse */}
      <Journey onOpenScheme={onOpenScheme} />
      <PathFinder onOpenScheme={onOpenScheme} onAskAi={onAskAi} />
      <StatesPanel onOpenScheme={onOpenScheme} onAskAi={onAskAi} />
      <Browse
        state={browse}
        onChange={(patch) => setBrowse((s) => ({ ...s, ...patch }))}
        onOpenScheme={onOpenScheme}
      />
    </>
  );
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function TricolourRibbon() {
  return (
    <span aria-hidden className="inline-flex items-center gap-[2px] h-3 opacity-80">
      <span className="block w-10 h-[3px] bg-gradient-to-r from-[#ff9933]/80 to-[#ff9933]/30 rounded-full" />
      <span className="block w-10 h-[3px] bg-cream-50/60 rounded-full" />
      <span className="block w-10 h-[3px] bg-gradient-to-r from-[#138808]/30 to-[#138808]/80 rounded-full" />
    </span>
  );
}

/* ---------- quick-nav cards (above-fold section index) ---------- */
const NAV_CARDS: Array<{ ordinal: string; label: string; hint: string; href: string }> = [
  { ordinal: "I",   label: "Journeys",   hint: "three roads · day 1", href: "#journey" },
  { ordinal: "II",  label: "Compass",    hint: "find your stack",     href: "#path" },
  { ordinal: "III", label: "Ministries", hint: "twelve halls",        href: "#ministries" },
  { ordinal: "IV",  label: "Personas",   hint: "nine founders",       href: "#personas" },
  { ordinal: "V",   label: "States",     hint: "thirty-five",         href: "#states" },
  { ordinal: "VI",  label: "Ledger",     hint: "browse everything",   href: "#browse" },
];

const MINISTRY_TILES = [
  { slug: "dpiit",    name: "DPIIT",              sigil: "डी" },
  { slug: "msme",     name: "Ministry of MSME",   sigil: "सू" },
  { slug: "dst",      name: "DST",                sigil: "वि" },
  { slug: "birac",    name: "BIRAC",              sigil: "जै" },
  { slug: "aim",      name: "Atal Innovation",    sigil: "अ" },
  { slug: "meity",    name: "MeitY",              sigil: "डिजि" },
  { slug: "niti",     name: "NITI Aayog",         sigil: "नीति" },
  { slug: "nabard",   name: "NABARD",             sigil: "ग्रा" },
  { slug: "sidbi",    name: "SIDBI",              sigil: "सिडबी" },
  { slug: "kvic",     name: "KVIC",               sigil: "खादी" },
  { slug: "textile",  name: "Textiles",           sigil: "वस्त्र" },
  { slug: "foodproc", name: "Food Processing",    sigil: "आहा" },
];

const PERSONA_TILES = [
  { slug: "bengaluru-deeptech",  label: "The Bengaluru deep-techer",     tag: "blr · deeptech",   teaser: "Taped out a prototype, closed angel, now the govt programmes are talking to you.", tint: "rgba(255,183,3,0.22)" },
  { slug: "surat-manufacturer",  label: "The Surat manufacturer",        tag: "gj · textile",     teaser: "Looms, polish lines, and a GST headache. Your stack is CGTMSE + CLCSS + Gujarat SSIP.", tint: "rgba(230,57,70,0.22)" },
  { slug: "kochi-woman-founder", label: "The Kochi woman founder",       tag: "kl · women",       teaser: "KSUM + WEP + Mahila Udyam Nidhi — the foundation, then DPIIT for the tax breaks.", tint: "rgba(42,157,143,0.22)" },
  { slug: "kanpur-leather",      label: "The Kanpur leather MSME",       tag: "up · leather",     teaser: "SFURTI cluster + ZED cert + PMEGP + CLCSS — the four pillars.", tint: "rgba(255,183,3,0.22)" },
  { slug: "chennai-saas",        label: "The Chennai SaaS bootstrapper", tag: "tn · saas",        teaser: "StartupTN's grant ladder + DPIIT + TN Fund-of-Funds, without dilution.", tint: "rgba(230,57,70,0.22)" },
  { slug: "guwahati-agritech",   label: "The Guwahati agritech",         tag: "as · agritech",    teaser: "AIM ACIC + NIDHI Seed + BIRAC AgriBio + Assam Startup Nest.", tint: "rgba(42,157,143,0.22)" },
  { slug: "kolkata-firstgen",    label: "The Kolkata first-gen",         tag: "wb · individual",  teaser: "Stand-Up India + Mudra Shishu + SC/ST/Women uplift.", tint: "rgba(255,183,3,0.22)" },
  { slug: "indore-food",         label: "The Indore food processor",     tag: "mp · food",        teaser: "PMFME is purpose-built. Plus PM-KISAN + CGTMSE + MP Startup Policy.", tint: "rgba(230,57,70,0.22)" },
  { slug: "imphal-rural",        label: "The Imphal rural innovator",    tag: "mn · rural",       teaser: "NIF's Micro Venture Innovation Fund, NIDHI PRAYAS, NER uplifts.", tint: "rgba(42,157,143,0.22)" },
];
